import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Plus, Edit, Trash2, GripVertical, Play, FileText, HelpCircle, Loader2, Save, ChevronDown, ChevronUp } from 'lucide-react';

const defaultSection = { title: '', order: 0 };
const defaultLesson = { title: '', type: 'video', content_url: '', content_text: '', duration_minutes: 0, is_preview: false, order: 0, quiz: null, assignment: null };

export default function CourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  
  const [showSectionDialog, setShowSectionDialog] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState(defaultSection);
  
  const [showLessonDialog, setShowLessonDialog] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState(defaultLesson);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);

  const fetchCourse = useCallback(async () => {
    try {
      const [courseRes, sectionsRes] = await Promise.all([
        api.get(`/tenant/courses/${courseId}`),
        api.get(`/tenant/courses/${courseId}/sections`)
      ]);
      setCourse(courseRes.data);
      setSections(sectionsRes.data.sections || []);
      const expanded = {};
      (sectionsRes.data.sections || []).forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { fetchCourse(); }, [fetchCourse]);

  const handleSaveSection = async () => {
    setSaving(true);
    try {
      if (editingSection) {
        await api.put(`/tenant/courses/${courseId}/sections/${editingSection}`, sectionForm);
      } else {
        await api.post(`/tenant/courses/${courseId}/sections`, { ...sectionForm, order: sections.length });
      }
      setShowSectionDialog(false);
      setEditingSection(null);
      setSectionForm(defaultSection);
      fetchCourse();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Hapus section ini beserta semua lesson di dalamnya?')) return;
    try {
      await api.delete(`/tenant/courses/${courseId}/sections/${sectionId}`);
      fetchCourse();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveLesson = async () => {
    setSaving(true);
    try {
      const lessonData = { ...lessonForm };
      if (quizQuestions.length > 0) {
        lessonData.quiz = quizQuestions;
      }
      if (editingLesson) {
        await api.put(`/tenant/courses/${courseId}/lessons/${editingLesson}`, lessonData);
      } else {
        const section = sections.find(s => s.id === currentSectionId);
        const order = section?.lessons?.length || 0;
        await api.post(`/tenant/courses/${courseId}/sections/${currentSectionId}/lessons`, { ...lessonData, order });
      }
      setShowLessonDialog(false);
      setEditingLesson(null);
      setLessonForm(defaultLesson);
      setQuizQuestions([]);
      setCurrentSectionId(null);
      fetchCourse();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Hapus lesson ini?')) return;
    try {
      await api.delete(`/tenant/courses/${courseId}/lessons/${lessonId}`);
      fetchCourse();
    } catch (e) {
      console.error(e);
    }
  };

  const openAddSection = () => {
    setEditingSection(null);
    setSectionForm(defaultSection);
    setShowSectionDialog(true);
  };

  const openEditSection = (section) => {
    setEditingSection(section.id);
    setSectionForm({ title: section.title, order: section.order });
    setShowSectionDialog(true);
  };

  const openAddLesson = (sectionId) => {
    setEditingLesson(null);
    setLessonForm(defaultLesson);
    setQuizQuestions([]);
    setCurrentSectionId(sectionId);
    setShowLessonDialog(true);
  };

  const openEditLesson = (lesson, sectionId) => {
    setEditingLesson(lesson.id);
    setLessonForm(lesson);
    setQuizQuestions(lesson.quiz || []);
    setCurrentSectionId(sectionId);
    setShowLessonDialog(true);
  };

  const addQuizQuestion = () => {
    setQuizQuestions([...quizQuestions, { question: '', options: ['', '', '', ''], correct_index: 0 }]);
  };

  const updateQuizQuestion = (index, field, value) => {
    const updated = [...quizQuestions];
    if (field === 'options') {
      updated[index].options = value;
    } else {
      updated[index][field] = value;
    }
    setQuizQuestions(updated);
  };

  const removeQuizQuestion = (index) => {
    setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <Play size={14} className="text-[#2D6CDF]" />;
      case 'text': return <FileText size={14} className="text-emerald-500" />;
      case 'quiz': return <HelpCircle size={14} className="text-amber-500" />;
      default: return <FileText size={14} />;
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div data-testid="course-editor-page">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/courses')} data-testid="back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>{course?.title}</h1>
          <p className="text-sm text-slate-500">Kelola konten kursus</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Sections & Lessons</h2>
            <Button onClick={openAddSection} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" size="sm" data-testid="add-section-btn">
              <Plus size={14} className="mr-1" /> Tambah Section
            </Button>
          </div>

          {sections.map((section, sIdx) => (
            <Card key={section.id} className="border-slate-200 shadow-sm">
              <CardHeader className="py-3 px-4 cursor-pointer" onClick={() => toggleSection(section.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GripVertical size={16} className="text-slate-400" />
                    <span className="text-xs font-medium text-slate-400">Section {sIdx + 1}</span>
                    <CardTitle className="text-base">{section.title}</CardTitle>
                    <span className="text-xs text-slate-500">({section.lessons?.length || 0} lessons)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEditSection(section); }} data-testid={`edit-section-${section.id}`}>
                      <Edit size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }} data-testid={`delete-section-${section.id}`}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                    {expandedSections[section.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
              </CardHeader>
              {expandedSections[section.id] && (
                <CardContent className="pt-0 pb-4 px-4">
                  <div className="space-y-2 ml-6">
                    {section.lessons?.map((lesson, lIdx) => (
                      <div key={lesson.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-3">
                          <GripVertical size={14} className="text-slate-300" />
                          {getLessonIcon(lesson.type)}
                          <div>
                            <p className="text-sm font-medium text-slate-900">{lesson.title}</p>
                            <p className="text-xs text-slate-500">{lesson.type} • {lesson.duration_minutes} menit {lesson.is_preview && '• Preview'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditLesson(lesson, section.id)} data-testid={`edit-lesson-${lesson.id}`}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteLesson(lesson.id)} data-testid={`delete-lesson-${lesson.id}`}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => openAddLesson(section.id)} className="w-full mt-2" data-testid={`add-lesson-${section.id}`}>
                      <Plus size={14} className="mr-1" /> Tambah Lesson
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}

          {!sections.length && (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="py-12 text-center">
                <p className="text-slate-400 mb-4">Belum ada section. Tambahkan section pertama untuk memulai.</p>
                <Button onClick={openAddSection} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]">
                  <Plus size={14} className="mr-1" /> Tambah Section
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          <Card className="border-slate-200 shadow-sm sticky top-4">
            <CardHeader>
              <CardTitle className="text-base">Info Kursus</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className={`font-medium ${course?.is_published ? 'text-emerald-600' : 'text-amber-600'}`}>{course?.is_published ? 'Published' : 'Draft'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Harga</span><span className="font-medium">Rp {new Intl.NumberFormat('id-ID').format(course?.price || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Instructor</span><span className="font-medium">{course?.instructor || '-'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Enrolled</span><span className="font-medium">{course?.enrollment_count || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Sections</span><span className="font-medium">{sections.length}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total Lessons</span><span className="font-medium">{sections.reduce((a, s) => a + (s.lessons?.length || 0), 0)}</span></div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section Dialog */}
      <Dialog open={showSectionDialog} onOpenChange={setShowSectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSection ? 'Edit Section' : 'Tambah Section'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Judul Section</Label>
              <Input value={sectionForm.title} onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })} placeholder="Contoh: Pengenalan" data-testid="section-title-input" />
            </div>
            <Button onClick={handleSaveSection} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-section-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-2" />}
              <Save size={14} className="mr-2" /> Simpan
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={showLessonDialog} onOpenChange={setShowLessonDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLesson ? 'Edit Lesson' : 'Tambah Lesson'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Judul Lesson</Label>
              <Input value={lessonForm.title} onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })} placeholder="Contoh: Apa itu React?" data-testid="lesson-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipe Konten</Label>
                <Select value={lessonForm.type} onValueChange={(v) => setLessonForm({ ...lessonForm, type: v })}>
                  <SelectTrigger data-testid="lesson-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="text">Teks/Artikel</SelectItem>
                    <SelectItem value="quiz">Quiz</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Durasi (menit)</Label>
                <Input type="number" value={lessonForm.duration_minutes} onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            {lessonForm.type === 'video' && (
              <div>
                <Label>URL Video (YouTube/Vimeo)</Label>
                <Input value={lessonForm.content_url} onChange={(e) => setLessonForm({ ...lessonForm, content_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." data-testid="lesson-video-url" />
              </div>
            )}
            {lessonForm.type === 'text' && (
              <div>
                <Label>Konten Teks</Label>
                <Textarea value={lessonForm.content_text} onChange={(e) => setLessonForm({ ...lessonForm, content_text: e.target.value })} rows={6} placeholder="Tulis konten lesson..." data-testid="lesson-text-content" />
              </div>
            )}
            {lessonForm.type === 'quiz' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Quiz Questions</Label>
                  <Button variant="outline" size="sm" onClick={addQuizQuestion} data-testid="add-quiz-question-btn">
                    <Plus size={14} className="mr-1" /> Tambah Soal
                  </Button>
                </div>
                {quizQuestions.map((q, qIdx) => (
                  <Card key={qIdx} className="border-slate-200">
                    <CardContent className="pt-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Soal {qIdx + 1}</Label>
                        <Button variant="ghost" size="icon" onClick={() => removeQuizQuestion(qIdx)}>
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </div>
                      <Input value={q.question} onChange={(e) => updateQuizQuestion(qIdx, 'question', e.target.value)} placeholder="Pertanyaan..." />
                      <div className="grid grid-cols-2 gap-2">
                        {q.options.map((opt, oIdx) => (
                          <div key={oIdx} className="flex items-center gap-2">
                            <input type="radio" name={`correct-${qIdx}`} checked={q.correct_index === oIdx} onChange={() => updateQuizQuestion(qIdx, 'correct_index', oIdx)} />
                            <Input value={opt} onChange={(e) => {
                              const newOpts = [...q.options];
                              newOpts[oIdx] = e.target.value;
                              updateQuizQuestion(qIdx, 'options', newOpts);
                            }} placeholder={`Opsi ${oIdx + 1}`} className="flex-1" />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={lessonForm.is_preview} onCheckedChange={(v) => setLessonForm({ ...lessonForm, is_preview: v })} />
              <Label>Jadikan Preview (bisa diakses tanpa enroll)</Label>
            </div>
            <Button onClick={handleSaveLesson} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-lesson-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-2" />}
              <Save size={14} className="mr-2" /> Simpan Lesson
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
